/**
 * Requêtes HTTP bas niveau vers le WMS du SMC.
 *
 * GetFeatureInfo : retourne la valeur d'une variable à un point et un instant donnés.
 */

const WMS_BASE_URL = 'https://geo.weather.gc.ca/geomet?SERVICE=WMS&VERSION=1.3.0';

/**
 * Construit l'URL GetFeatureInfo WMS.
 *
 * @param {object} params
 * @param {string} params.layer       - ex: 'HRDPS.CONTINENTAL_TT'
 * @param {number} params.lat
 * @param {number} params.lon
 * @param {string} params.time        - ISO 8601 UTC ex: '2023-05-13T06:00:00Z'
 * @param {string} params.refTime     - ISO 8601 UTC : origine de la prévision (dateo)
 * @param {string} [params.style=''] - style WMS à forcer (ex: 'GUST_MS2KMH'); '' = style par défaut
 * @returns {string} URL complète
 */
export function buildFeatureInfoUrl({ layer, lat, lon, time, refTime, style = '' }) {
  const delta = 0.0001;
  const bbox = `${lat - delta},${lon - delta},${lat + delta},${lon + delta}`;
  return (
    WMS_BASE_URL +
    '&REQUEST=GetFeatureInfo' +
    '&INFO_FORMAT=application/json' +
    '&WIDTH=10&HEIGHT=10&I=5&J=5' +
    `&LAYERS=${layer}` +
    `&QUERY_LAYERS=${layer}` +
    (style ? `&STYLES=${style}` : '') +
    '&CRS=EPSG:4326' +
    `&BBOX=${bbox}` +
    `&TIME=${time}` +
    `&DIM_REFERENCE_TIME=${refTime}`
  );
}

/**
 * Formate une Date en chaîne ISO 8601 UTC tronquée à la minute.
 * ex: '2023-05-13T06:00:00Z'
 *
 * @param {Date} date
 * @returns {string}
 */
export function formatWmsTime(date) {
  return date.toISOString().slice(0, 19) + 'Z';
}

/**
 * Fait une requête fetch avec retry automatique sur HTTP 429.
 *
 * @param {string} url
 * @param {RequestInit} fetchOpts
 * @param {number} [maxRetries=3]
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(url, fetchOpts, maxRetries = 3) {
  let delay = 2000;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const resp = await fetch(url, fetchOpts);
    if (resp.status !== 429 || attempt === maxRetries) return resp;
    await new Promise(resolve => setTimeout(resolve, delay));
    delay *= 2;
  }
}

/**
 * Fait une requête GetFeatureInfo et retourne la valeur numérique.
 *
 * @param {object} params     - mêmes paramètres que buildFeatureInfoUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<{value: number|null, gridLon: number, gridLat: number}>}
 *   value est null si le point est hors grille ou sans données
 */
export async function fetchFeatureInfo(params, signal) {
  const url = buildFeatureInfoUrl(params);
  const resp = await fetchWithRetry(url, signal ? { signal } : {});
  if (!resp.ok) throw new Error(`GetFeatureInfo HTTP ${resp.status}`);

  let json;
  try {
    json = await resp.json();
  } catch {
    // Le WMS retourne parfois une exception XML avec HTTP 200
    return { value: null, gridLon: null, gridLat: null };
  }
  const feature = json?.features?.[0];
  if (!feature) return { value: null, gridLon: null, gridLat: null };

  const value = feature.properties?.value;
  const [gridLon, gridLat] = feature.geometry?.coordinates ?? [null, null];

  return {
    value:   value != null ? parseFloat(value) : null,
    gridLon,
    gridLat,
  };
}

/**
 * Exécute une liste de tâches asynchrones avec au plus `concurrency` tâches
 * en vol simultanément, pour éviter de saturer le serveur WMS (HTTP 429) ou
 * le nombre de connexions simultanées du navigateur.
 *
 * @template T
 * @param {T[]} tasks
 * @param {(task: T) => Promise<void>} worker - traite une tâche ; les erreurs
 *   doivent être gérées à l'intérieur (une erreur non rattrapée interrompt tout).
 * @param {number} concurrency - nombre de tâches traitées en parallèle
 * @returns {Promise<void>}
 */
export async function runWithConcurrency(tasks, worker, concurrency) {
  const queue = [...tasks];
  async function runOne() {
    while (queue.length > 0) {
      const task = queue.shift();
      await worker(task);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, runOne)
  );
}

/**
 * Filtre une liste de timestamps disponibles selon une fenêtre et un intervalle souhaité.
 *
 * Pour les modèles à intervalles non uniformes (ex: GDPS 1h puis 3h), retourne le
 * maximum de points disponibles compatibles avec l'intervalle demandé :
 *   deltaHours=1 → tous les timestamps présents dans la liste
 *   deltaHours=3 → seulement les timestamps dont l'heure depuis start est multiple de 3
 *
 * @param {Date[]} times    - timestamps disponibles, triés (depuis parseCapabilities)
 * @param {Date}   start    - début de la fenêtre
 * @param {number} hours    - durée de la fenêtre en heures depuis start
 * @param {number|Array<{until?: number, step: number}>} deltaHours
 *   Intervalle souhaité (nombre), ou tableau de segments [{until?, step}].
 * @param {number} [startHour=0]
 * @returns {Date[]}
 */
export function filterTimes(times, start, hours, deltaHours, startHour = 0) {
  const t0 = start.getTime();
  const isSchedule = Array.isArray(deltaHours);
  return times.filter(t => {
    const h = Math.round((t.getTime() - t0) / 3_600_000);
    if (h < startHour || h > hours) return false;
    const step = isSchedule
      ? (deltaHours.find(s => s.until === undefined || h < s.until) ?? deltaHours[deltaHours.length - 1]).step
      : deltaHours;
    return h % step === 0;
  });
}

/**
 * Génère la liste des instants UTC à récupérer.
 *
 * @param {Date}   dateo      - origine de la prévision
 * @param {number} hours      - horizon total en heures
 * @param {number|Array<{until?: number, step: number}>} deltaHours
 *   Pas de temps uniforme (nombre), ou tableau de segments à intervalles variables.
 *   Ex: [{until: 12, step: 1}, {step: 3}] → toutes les heures jusqu'à H+12, puis toutes les 3h.
 *   Le dernier segment (sans `until`) s'applique jusqu'à la fin.
 * @param {number} [startHour=0]
 *   Première heure à inclure (les heures < startHour sont ignorées).
 *   Utile pour ne charger que les données à partir de l'heure courante.
 * @returns {{times: string[], hhh: number[]}}
 *   times : tableau d'instants ISO 8601
 *   hhh   : tableau des heures écoulées depuis dateo (0, 1, 3, ...)
 */
export function buildTimeSteps(dateo, hours, deltaHours, startHour = 0) {
  const schedule = Array.isArray(deltaHours) ? deltaHours : null;
  const times = [];
  const hhh   = [];

  let h = 0;
  while (h <= hours) {
    let step;
    if (schedule) {
      const seg = schedule.find(s => s.until === undefined || h < s.until) ?? schedule[schedule.length - 1];
      step = seg.step;
    } else {
      step = deltaHours;
    }

    if (h >= startHour) {
      const t = new Date(dateo.getTime() + h * 3600 * 1000);
      times.push(formatWmsTime(t));
      hhh.push(h);
    }
    h += step;
  }
  return { times, hhh };
}
